import "./IDTable.scss";
import React from "react";
import { AnimatePresence, motion } from "framer-motion";

import { useRowSelect, useTable, useFilters, useSortBy } from "react-table";
import { block } from "bem-cn";
import { useEffect } from "react";
import { TBusyValue } from "../BusyComboBox/BusyComboBox.utils";
import { useAppDispatch } from "../../app/hooks";
import { setSelectedDevelopers } from "../App/AppSlice";
import {
  DefaultColumnFilter,
  filterByBusy,
  filterByTags,
  filterByText,
  fuzzyTextFilterFn,
  setCellContent,
} from "./IDTable.utils";
import Skeleton from "react-loading-skeleton";

const b = block("IDTable");

type Props = {
  type: string;
  columns: any;
  data: any;
  filterValue: tagList;
  filterBusy: TBusyValue[];
  isAuth: boolean;
};

// function shuffle(arr: any) {
//   arr = [...arr];
//   const shuffled = [];
//   while (arr.length) {
//     const rand = Math.floor(Math.random() * arr.length);
//     shuffled.push(arr.splice(rand, 1)[0]);
//   }
//   return shuffled;
// }

const IDTable = ({
  type = "ФИО",
  columns = [],
  data = [],
  filterValue: text,
  filterBusy,
  isAuth = false,
}: Props) => {
  const filterTypes = React.useMemo(
    () => ({
      fuzzyText: fuzzyTextFilterFn,
      text: filterByText,
      byTags: filterByTags,
      byBusy: filterByBusy,
    }),
    []
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const dispatch = useAppDispatch();

  const defaultColumn = React.useMemo(
    () => ({
      // Let's set up our default Filter UI
      Filter: DefaultColumnFilter,
    }),
    []
  );

  const c = React.useMemo(() => columns, [columns]);
  const d = React.useMemo(() => data, [data]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    selectedFlatRows,
    state,
    state: { selectedRowIds },
    setFilter,
  } = useTable(
    {
      columns: c,
      data: d,
      defaultColumn, // Be sure to pass the defaultColumn option
      filterTypes,
    },
    useFilters,
    useSortBy,
    useRowSelect
  );

  const spring = React.useMemo(
    () => ({
      type: "spring",
      damping: 50,
      stiffness: 100,
    }),
    []
  );

  useEffect(() => {
    const list = selectedFlatRows.map((item) => item.values.name);
    dispatch(setSelectedDevelopers(list));
  }, [dispatch, selectedFlatRows]);

  useEffect(() => {
    setFilter("stack", text);
    setFilter("busy", filterBusy);
  }, [setFilter, text, filterBusy]);

  return (
    <>
      <table {...getTableProps()} className={b()}>
        <thead className={b("head")}>
          {headerGroups.map((headerGroup) => (
            <tr
              className={b("head-row")}
              {...headerGroup.getHeaderGroupProps()}
            >
              {headerGroup.headers.map((column, index) => {
                const id = headerGroups[0].headers[index].id;
                const desc = column.isSorted && column.isSortedDesc;
                const asc = column.isSorted && !column.isSortedDesc;
                const enableSortingForSomeColumns = ["exp", "busy"].includes(
                  column.id
                )
                  ? column.getSortByToggleProps()
                  : {};

                return (
                  <th
                    className={b("head-cell", { taLeft: id === "name" })}
                    {...column.getHeaderProps(enableSortingForSomeColumns)}
                  >
                    <div className={b("head-cell-wrapper", { id, desc, asc })}>
                      {column.render("Header")}
                    </div>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        {data.length === 0 ? (
          <tbody>
            {Array(20)
              .fill(1)
              .map(() => (
                <tr>
                  {headerGroups.map((headerGroup) =>
                    headerGroup.headers.map((cell, index) => {
                      const id = headerGroups[0].headers[index].id;

                      return (
                        <td
                          className={b("body-cell", {
                            pdl: id === "name",
                            taLeft: id === "name",
                          })}
                          style={{ padding: "10px" }}
                        >
                          <Skeleton height={23} style={{ borderRadius: 10 }} />
                        </td>
                      );
                    })
                  )}
                </tr>
              ))}
          </tbody>
        ) : (
          <tbody {...getTableBodyProps()}>
            <AnimatePresence>
              {rows.map((row) => {
                prepareRow(row);
                return (
                  <motion.tr
                    className={b("body-row")}
                    {...row.getRowProps({
                      layoutTransition: spring,
                      exit: { opacity: 0, maxHeight: 0 },
                    } as any)}
                  >
                    {row.cells.map((cell, index) => {
                      const id = headerGroups[0].headers[index].id;
                      return (
                        <motion.td
                          className={b("body-cell", {
                            pdl: id === "name",
                            taLeft: id === "name",
                          })}
                          {...cell.getCellProps({
                            layoutTransition: spring,
                          } as any)}
                        >
                          {setCellContent(
                            index,
                            row,
                            cell,
                            headerGroups,
                            isAuth,
                            b
                          )}
                        </motion.td>
                      );
                    })}
                  </motion.tr>
                );
              })}
            </AnimatePresence>
          </tbody>
        )}
      </table>

      <div hidden={true}>
        <br />
        <div>Showing the first 20 results of {rows.length} rows</div>
        <div>
          <pre>
            <code>{JSON.stringify(state.filters, null, 2)}</code>
          </pre>
        </div>

        <p>Selected Rows: {Object.keys(selectedRowIds).length}</p>
        <pre>
          <code>
            {JSON.stringify(
              {
                selectedRowIds: selectedRowIds,
                "selectedFlatRows[].original": selectedFlatRows.map(
                  (d) => d.original
                ),
              },
              null,
              2
            )}
          </code>
        </pre>
      </div>
    </>
  );
};

export default IDTable;
